import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Users, UserPlus, AlertCircle, Copy, Link as LinkIcon, QrCode, Download, XCircle, Trash2, MousePointerClick } from "lucide-react";
import { API } from "../App";
import { useI18n } from "../lib/i18n";
import { formatDateSafe } from "../lib/formatDate";

const DATE_OPTS = { day: "numeric", month: "long", year: "numeric" };

const PatientsHome = ({ onPatientsChanged, onPendingCount }) => {
  const { t } = useI18n();
  const [pendingPatients, setPendingPatients] = useState([]);
  const [invitationCode, setInvitationCode] = useState("");
  const [showQR, setShowQR] = useState(false);
  const [deletionRequests, setDeletionRequests] = useState([]);

  const load = () => {
    axios.get(`${API}/therapist/patients/pending`, { withCredentials: true }).then((r) => { setPendingPatients(r.data); onPendingCount?.(r.data.length); }).catch(() => {});
    axios.get(`${API}/auth/invitation-code`, { withCredentials: true }).then((r) => setInvitationCode(r.data.invitation_code)).catch(() => {});
    axios.get(`${API}/therapist/deletion-requests`).then((r) => setDeletionRequests(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); load(); onPatientsChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const claim = (id) => act(() => axios.post(`${API}/therapist/patients/claim/${id}`, {}, { withCredentials: true }), t("patientMgmt.patientClaimed"));
  const refuse = (id) => window.confirm(t("patientMgmt.confirmRefuse", "Voulez-vous vraiment refuser ce patient ?")) && act(() => axios.post(`${API}/therapist/patients/refuse/${id}`, {}, { withCredentials: true }), t("patientMgmt.patientRefused", "Patient refusé"));
  const approveDeletion = (id) => window.confirm("Confirmer la suppression définitive de toutes les données de ce patient ?") && act(() => axios.post(`${API}/therapist/deletion-requests/${id}/approve`), "Données supprimées avec succès");
  const rejectDeletion = (id) => { const reason = window.prompt("Motif du refus :"); if (reason) act(() => axios.post(`${API}/therapist/deletion-requests/${id}/reject`, { rejection_reason: reason }), "Demande refusée"); };

  const copyInvitationLink = () => { navigator.clipboard.writeText(`${window.location.origin}/bienvenue?ref=${invitationCode}`); toast.success(t("patientMgmt.linkCopied", "Lien d'invitation copié !")); };
  const downloadQR = () => {
    const svg = document.getElementById("invitation-qr");
    if (!svg) return;
    const canvas = document.createElement("canvas"); canvas.width = 512; canvas.height = 512;
    const ctx = canvas.getContext("2d"); const img = new Image();
    img.onload = () => { ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, 512, 512); ctx.drawImage(img, 0, 0, 512, 512); const a = document.createElement("a"); a.download = `invitation-${invitationCode}.png`; a.href = canvas.toDataURL("image/png"); a.click(); };
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(new XMLSerializer().serializeToString(svg))));
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="patients-home">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{t("patientMgmt.title")}</h1>
        <p className="text-muted-foreground text-sm sm:text-base mt-1">{t("patientMgmt.subtitle")}</p>
      </div>

      <div className="hidden md:flex items-center gap-3 p-4 rounded-xl border border-dashed text-muted-foreground" data-testid="select-patient-hint">
        <MousePointerClick className="w-5 h-5 shrink-0" />
        <p className="text-sm">Sélectionnez un patient dans la liste pour ouvrir son espace de travail : progression, exercices, comptes rendus, e-mails de suivi et synthèse clinique.</p>
      </div>

      {invitationCode && (
        <Card className="border-primary/30 bg-primary/5" data-testid="invitation-code-card">
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3"><LinkIcon className="w-5 h-5 text-primary flex-shrink-0" />
                <div><p className="text-sm font-medium">{t("patientMgmt.invitationCode")}</p><p className="text-xs text-muted-foreground">{t("patientMgmt.invitationCodeDesc")}</p></div></div>
              <div className="flex items-center gap-2 flex-wrap">
                <code className="px-3 py-1.5 bg-muted rounded-md text-xs sm:text-sm font-mono truncate max-w-[160px] sm:max-w-none" data-testid="invitation-code-value">{invitationCode}</code>
                <Button variant="outline" size="sm" onClick={copyInvitationLink} className="gap-1.5" data-testid="copy-invitation-link-btn"><Copy className="w-4 h-4" /><span className="hidden sm:inline">{t("patientMgmt.copyLink")}</span></Button>
                <Button variant="outline" size="sm" onClick={() => setShowQR(!showQR)} className="gap-1.5" data-testid="toggle-qr-btn"><QrCode className="w-4 h-4" /> QR</Button>
              </div>
            </div>
            {showQR && (
              <div className="mt-4 flex flex-col items-center gap-3 p-4 bg-white rounded-lg border" data-testid="qr-code-section">
                <QRCodeSVG id="invitation-qr" value={`${window.location.origin}/bienvenue?ref=${invitationCode}`} size={200} level="H" includeMargin />
                <p className="text-xs text-muted-foreground text-center">{t("patientMgmt.qrScanDesc", "Scannez ce QR code pour accéder au formulaire d'inscription.")}</p>
                <Button variant="outline" size="sm" onClick={downloadQR} className="gap-2" data-testid="download-qr-btn"><Download className="w-4 h-4" />{t("patientMgmt.downloadQR", "Télécharger le QR code")}</Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {pendingPatients.length > 0 && (
        <Card className="border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20" data-testid="pending-patients-section">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2 text-amber-700 dark:text-amber-300"><AlertCircle className="w-5 h-5" />{t("patientMgmt.pendingPatients", "Patients en attente")} ({pendingPatients.length})</CardTitle>
            <p className="text-sm text-muted-foreground">{t("patientMgmt.pendingPatientsDesc", "Ces patients se sont inscrits mais ne sont rattachés à aucun thérapeute.")}</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingPatients.map((p) => (
              <div key={p.user_id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border border-amber-200 dark:border-amber-800 rounded-lg bg-card" data-testid={`pending-patient-${p.user_id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center flex-shrink-0"><span className="text-amber-700 dark:text-amber-300 font-semibold">{p.name?.charAt(0)?.toUpperCase() || "?"}</span></div>
                  <div className="min-w-0"><p className="font-medium truncate">{p.name || t("patientMgmt.unnamed", "Sans nom")}</p><p className="text-sm text-muted-foreground truncate">{p.email}</p></div>
                </div>
                <div className="flex items-center gap-2 sm:ml-auto">
                  <Button onClick={() => claim(p.user_id)} size="sm" className="gap-1.5 bg-amber-600 hover:bg-amber-700 text-white" data-testid={`claim-patient-${p.user_id}`}><UserPlus className="w-4 h-4" />{t("patientMgmt.claimPatient")}</Button>
                  <Button variant="outline" size="sm" onClick={() => refuse(p.user_id)} className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10" data-testid={`refuse-patient-${p.user_id}`}><XCircle className="w-4 h-4" />{t("patientMgmt.refusePatient", "Refuser")}</Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {deletionRequests.length > 0 && (
        <Card className="border-red-300 dark:border-red-700 bg-red-50/50 dark:bg-red-950/20" data-testid="deletion-requests-section">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2 text-red-700 dark:text-red-300"><Trash2 className="w-5 h-5" /> Demandes de suppression ({deletionRequests.length})</CardTitle>
            <p className="text-sm text-muted-foreground">Demandes de suppression de données conformes au RGPD. La rétention HAS de 20 ans s'applique.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {deletionRequests.map((req) => (
              <div key={req.request_id} className="p-4 border border-red-200 dark:border-red-800 rounded-lg bg-card flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`deletion-req-${req.request_id}`}>
                <div>
                  <p className="font-medium">{req.patient_name}</p><p className="text-sm text-muted-foreground">{req.patient_email}</p>
                  {req.reason && <p className="text-sm mt-1">Motif : {req.reason}</p>}
                  <p className="text-xs text-muted-foreground mt-1">Demandé le {formatDateSafe(req.requested_at, DATE_OPTS)}{!req.is_eligible && <span className="text-amber-600 ml-2">Rétention HAS jusqu'au {formatDateSafe(req.eligible_date, DATE_OPTS)}</span>}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="destructive" onClick={() => approveDeletion(req.request_id)} disabled={!req.is_eligible} data-testid={`approve-deletion-${req.request_id}`}>{req.is_eligible ? "Approuver" : "Bloqué (HAS)"}</Button>
                  <Button size="sm" variant="outline" onClick={() => rejectDeletion(req.request_id)} data-testid={`reject-deletion-${req.request_id}`}>Refuser</Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {pendingPatients.length === 0 && deletionRequests.length === 0 && (
        <div className="flex items-center gap-3 text-sm text-muted-foreground"><Users className="w-4 h-4" /> Aucune demande en attente.</div>
      )}
    </div>
  );
};

export default PatientsHome;
